import { useEffect, useState, type ReactNode } from 'react';
import { isOutOfDate, onOutOfDate } from '../build.ts';
import { Link } from 'react-router-dom';
import { ClientSearch } from './ClientSearch.tsx';
import { ModuleTabs, Toolbar, TopMenu } from './Toolbar.tsx';
import { OpenTabsProvider, OpenTabsStrip } from './OpenTabs.tsx';
import { useToolbar } from '../api/navigation.ts';
import { QuickSearchProvider } from './QuickSearch.tsx';
import { useMe } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import { WordmarkText } from './WordmarkText.tsx';
import { AccountRefused, isAccountRefusal } from './AccountRefused.tsx';
import { AccountBadge } from './AccountBadge.tsx';

/**
 * The application shell (docs/navigation-brief.md): one brand bar holding the
 * wordmark, finding a client and the top-right menu, and under them, in the
 * same bar, the modules with the reader's counts (the approved mockup, Arthur,
 * 15 October); under that the clients open as tabs. Above every signed-in
 * screen. Two rows inside the one bar, so ten modules with their counts never
 * have to share a line with the search, and a tablet scrolls the modules
 * rather than wrapping them.
 *
 * A reader with no client pages (the receptionist) gets no search bar and no
 * Ctrl+F: their screen is the lookup, which answers who holds a client and
 * nothing else.
 *
 * **Ctrl+F (⌘F) is the quick search on every screen under it**, not the
 * browser's find bar: see QuickSearch.tsx. A list added later gets the search
 * by being here, and can let it jump to rows by marking them.
 *
 * **The bar scrolls away; it is not sticky.** A client's page pins its Critical
 * band to the top of the screen, full-bleed, above everything. A sticky bar
 * would sit over it, and that band is the one thing on the highest-risk screen
 * that cannot be argued about.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const toolbar = useToolbar();
  const { signOut } = useSession();
  const clientPages = toolbar.data?.client_pages ?? false;

  // The server refused the account itself (19 October 2026): say which, and
  // offer another, INSTEAD of the app, so no screen beneath shows its own error
  // as well. Only the brand bar stays, so it is still plainly Procura.
  if (isAccountRefusal(me.error)) {
    return (
      <>
        <header className="bg-brand text-on-brand">
          <div className="mx-auto flex max-w-[96rem] items-center px-4 py-2 lg:px-6"><Wordmark /></div>
        </header>
        <AccountRefused error={me.error} />
      </>
    );
  }

  return (
    <QuickSearchProvider enabled={clientPages}>
      <OpenTabsProvider>
        {me.data?.breakGlass && <BreakGlassBand />}
        <OutOfDateBand />
        <header className="bg-brand text-on-brand">
          <div className="mx-auto flex max-w-[96rem] flex-wrap items-center gap-x-6 gap-y-2 px-4 pt-2 pb-1 lg:flex-nowrap lg:px-6">
            <Wordmark />
            {clientPages && (
              <ClientSearch className="order-last w-full lg:order-none lg:w-auto lg:max-w-xl lg:flex-1" />
            )}
            <div className="ml-auto flex min-w-0 items-center gap-3">
              <TopMenu />
              {/* Who is signed in, beside Sign out (19 October): name and job title, the email on hover,
                  focus or tap (components/AccountBadge.tsx). */}
              {me.data && <AccountBadge me={me.data} />}
              {/* A reload after signing out, so nothing one person's session
                  fetched is still in memory when the next signs in. */}
              <button type="button" onClick={() => void signOut().then(() => location.assign('/'))}
                      className="inline-flex min-h-[44px] shrink-0 items-center rounded-lg px-2 text-sm font-semibold text-on-brand hover:underline">
                Sign out
              </button>
            </div>
          </div>
          <Toolbar />
        </header>
        <ModuleTabs />
        <OpenTabsStrip />
        {children}
      </OpenTabsProvider>
    </QuickSearchProvider>
  );
}

/**
 * Procura was updated while this page was open (build.ts). The page is
 * running the old code against the new server, and an action that needs the
 * new form fails in a way the old one cannot explain. Says so and offers the
 * reload; never reloads by itself, because somebody may be half way through a
 * form. Not by colour alone: the words carry it.
 */
function OutOfDateBand() {
  const [stale, setStale] = useState(isOutOfDate());
  useEffect(() => onOutOfDate(() => setStale(true)), []);
  if (!stale) return null;
  return (
    <div role="alert" className="border-b border-amber-400 bg-amber-50 text-amber-950">
      <div className="mx-auto flex max-w-[96rem] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 lg:px-6">
        <p className="font-semibold">Procura has been updated since this page was opened.</p>
        <p className="text-sm">Reload to use the new version. Anything typed and not yet saved will need typing again.</p>
        <button type="button" onClick={() => location.reload()}
                className="ml-auto min-h-[44px] rounded-lg bg-amber-900 px-4 text-sm font-semibold text-white">
          Reload now
        </button>
      </div>
    </div>
  );
}

/** The wordmark, linking home (components/WordmarkText.tsx draws it). */
function Wordmark() {
  return (
    <Link to="/" aria-label="procura for Allied CSS, home" className="inline-flex min-h-[44px] shrink-0 items-center">
      <WordmarkText tone="on-brand" />
    </Link>
  );
}

/**
 * On every screen while the break-glass account is signed in (062).
 *
 * The person using it must not be able to forget it: every page they open and
 * every change they make is recorded against a break-glass session, and the
 * session's start has been reported to Arthur. Words, not only colour. It
 * scrolls away with the header rather than sticking, so it never sits over a
 * client's Critical band.
 */
function BreakGlassBand() {
  return (
    <div role="status" className="bg-rose-800 px-4 py-2 text-center text-[0.9375rem] font-semibold text-on-brand">
      Break-glass account. Every page you open and every change you make is recorded, and your use has
      been reported. Sign out when the emergency is over.
    </div>
  );
}
